import React, { useState, useEffect } from 'react';
import userService from '../../services/userService';
import '../../styles/Admin.css';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    phone: '',
    role: 'user',
    module: 'ARA',
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const data = await userService.getAllUsers();
      setUsers(data.users);
      setLoading(false);
    } catch (err) {
      setError('Erreur lors du chargement des utilisateurs');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    try {
      // Préparer les données en fonction du rôle
      const dataToSubmit = {
        ...formData,
        // Si le rôle est admin ou superviseur, le module doit être null
        module: (formData.role === 'admin' || formData.role === 'superviseur') ? null : formData.module
      };

      if (editingUser) {
        await userService.updateUser(editingUser.id, dataToSubmit);
        setMessage('Utilisateur mis à jour avec succès');
      } else {
        await userService.createUser(dataToSubmit);
        setMessage('Utilisateur créé avec succès');
      }

      setShowModal(false);
      resetForm();
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de l\'opération');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      phone: user.phone,
      role: user.role,
      module: user.module || 'ARA',
      password: '',
    });
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) {
      try {
        await userService.deleteUser(userId);
        setMessage('Utilisateur supprimé avec succès');
        fetchUsers();
      } catch (err) {
        setError('Erreur lors de la suppression');
      }
    }
  };

  const handleToggleActive = async (userId, isActive) => {
    try {
      if (isActive) {
        await userService.deactivateUser(userId);
        setMessage('Utilisateur désactivé');
      } else {
        await userService.activateUser(userId);
        setMessage('Utilisateur activé');
      }
      fetchUsers();
    } catch (err) {
      setError('Erreur lors de l\'opération');
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      password: '',
      phone: '',
      role: 'user',
      module: 'ARA',
    });
    setEditingUser(null);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  if (loading) return <div>Chargement...</div>;

  return (
    <div className="user-management">
      <div className="header">
        <h1>Gestion des utilisateurs</h1>
        <button
          className="btn-primary"
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
        >
          Nouvel utilisateur
        </button>
      </div>

      {message && <div className="success-message">{message}</div>}
      {error && <div className="error-message">{error}</div>}

      <table className="users-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Nom d'utilisateur</th>
            <th>Email</th>
            <th>Téléphone</th>
            <th>Rôle</th>
            <th>Module</th>
            <th>Statut</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => (
            <tr key={user.id}>
              <td>{user.id}</td>
              <td>{user.username}</td>
              <td>{user.email}</td>
              <td>{user.phone}</td>
              <td>{user.role}</td>
              <td>{user.module || '-'}</td>
              <td>
                <span className={`status ${user.is_active ? 'active' : 'inactive'}`}>
                  {user.is_active ? 'Actif' : 'Inactif'}
                </span>
              </td>
              <td>
                <button className="btn-small" onClick={() => handleEdit(user)}>
                  Modifier
                </button>
                <button
                  className="btn-small"
                  onClick={() => handleToggleActive(user.id, user.is_active)}
                >
                  {user.is_active ? 'Désactiver' : 'Activer'}
                </button>
                <button
                  className="btn-small btn-danger"
                  onClick={() => handleDelete(user.id)}
                >
                  Supprimer
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showModal && (
        <div className="modal">
          <div className="modal-content">
            <h2>{editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Nom d'utilisateur</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Mot de passe {editingUser && '(laisser vide pour ne pas changer)'}</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required={!editingUser}
                />
              </div>

              <div className="form-group">
                <label>Téléphone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>Rôle</label>
                <select name="role" value={formData.role} onChange={handleChange} required>
                  <option value="user">Utilisateur</option>
                  <option value="superviseur">Superviseur</option>
                  <option value="admin">Administrateur</option>
                </select>
              </div>

              {formData.role === 'user' && (
                <div className="form-group">
                  <label>Module</label>
                  <select name="module" value={formData.module} onChange={handleChange} required>
                    <option value="ARA">ARA</option>
                    <option value="ESSIE-EHOUN">ESSIE-EHOUN</option>
                  </select>
                </div>
              )}

              <div className="modal-actions">
                <button type="submit" className="btn-primary">
                  {editingUser ? 'Mettre à jour' : 'Créer'}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
